import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Spinner,
  Alert,
  Table,
  Badge,
} from 'react-bootstrap';
import statisticsService from '../../../components/Statistics/services/statisticsService';
import { showError } from '../../../Utilities/sweetAlert';
import { COLORS } from '../../constants/colors';
import './Statistics.css';

interface HikesPerTrail {
  trailId: number;
  trailName: string;
  hikeCount: number;
}

interface MostHikedTrail {
  trailId: number;
  trailName: string;
  hikeCount: number;
}

interface MostSavedTrail {
  trailId: number;
  trailName: string;
  saveCount: number;
}

interface RouteStats {
  totalRoutes: number;
  byState: { draft: number; personal: number; public: number };
  byStatus: { pending: number; active: number; inactive: number };
  pendingReview: number;
}

interface TopRouteCreator {
  userId: number;
  userName: string;
  userEmail: string;
  totalRoutes: number;
  activeRoutes: number;
  pendingRoutes: number;
  rejectedRoutes: number;
}

interface OnboardingStat {
  question: string;
  answers: {
    answer: string;
    count: number;
  }[];
}

const Statistics: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hikesPerTrail, setHikesPerTrail] = useState<HikesPerTrail[]>([]);
  const [mostHikedTrail, setMostHikedTrail] = useState<MostHikedTrail | null>(null);
  const [mostSavedTrail, setMostSavedTrail] = useState<MostSavedTrail | null>(null);
  const [hikesCompleted, setHikesCompleted] = useState<number>(0);
  const [hikesStarted, setHikesStarted] = useState<number>(0);
  const [monthlySubscribers, setMonthlySubscribers] = useState<number>(0);
  const [routeStats, setRouteStats] = useState<RouteStats>({
    totalRoutes: 0,
    byState: { draft: 0, personal: 0, public: 0 },
    byStatus: { pending: 0, active: 0, inactive: 0 },
    pendingReview: 0,
  });
  const [topCreators, setTopCreators] = useState<TopRouteCreator[]>([]);
  const [trailsRankedByCompleted, setTrailsRankedByCompleted] = useState<HikesPerTrail[]>([]);
  const [trailsRankedByAttempted, setTrailsRankedByAttempted] = useState<HikesPerTrail[]>([]);
  const [onboardingStats, setOnboardingStats] = useState<OnboardingStat[]>([]);

  useEffect(() => {
    const fetchStatistics = async () => {
      try {
        setLoading(true);
        setError(null);

        const [
          hikesPerTrailRes,
          mostHikedRes,
          mostSavedRes,
          completedRes,
          startedRes,
          subscribersRes,
          routeStatsRes,
          topCreatorsRes,
          completedRankingRes,
          attemptedRankingRes,
          onboardingStatsRes,
        ] = await Promise.all([
          statisticsService.getHikesCountPerTrail(),
          statisticsService.getMostHikedTrail(),
          statisticsService.getMostSavedTrail(),
          statisticsService.getHikesCompletedCount(),
          statisticsService.getHikesStartedCount(),
          statisticsService.getMonthlySubscribersCount(),
          statisticsService.getRouteStats(),
          statisticsService.getTopRouteCreators(),
          statisticsService.getTrailsRankedByCompletedHikes(),
          statisticsService.getTrailsRankedByAttemptedHikes(),
          statisticsService.getOnboardingStats(),
        ]);

        setHikesPerTrail(hikesPerTrailRes.data);
        setMostHikedTrail(mostHikedRes.data);
        setMostSavedTrail(mostSavedRes.data);
        setHikesCompleted(completedRes.data.count);
        setHikesStarted(startedRes.data.count);
        setMonthlySubscribers(subscribersRes.data.count);
        setRouteStats(routeStatsRes.data);
        setTopCreators(topCreatorsRes.data.creators || []);
        setTrailsRankedByCompleted((completedRankingRes.data as any).data || completedRankingRes.data || []);
        setTrailsRankedByAttempted((attemptedRankingRes.data as any).data || attemptedRankingRes.data || []);
        setOnboardingStats((onboardingStatsRes.data as any).data || onboardingStatsRes.data || []);
      } catch (err: any) {
        console.error('Error fetching statistics:', err);
        setError('Failed to fetch statistics');
        showError('Failed to fetch statistics');
      } finally {
        setLoading(false);
      }
    };

    fetchStatistics();
  }, []);

  if (loading) {
    return (
      <Container fluid className="statistics-container py-4">
        <div className="text-center py-5">
          <Spinner animation="border" />
          <p className="mt-2">Loading statistics...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid className="statistics-container py-4">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  const approvalRate = routeStats.byStatus.active + routeStats.byStatus.inactive > 0
    ? ((routeStats.byStatus.active / (routeStats.byStatus.active + routeStats.byStatus.inactive)) * 100).toFixed(1)
    : '0.0';

  return (
    <Container fluid className="statistics-container py-4">
      <Row className="mb-4">
        <Col>
          <Card className="mb-4">
            <Card.Header style={{ backgroundColor: COLORS.BUTTON_GREEN, color: COLORS.WHITE }}>
              <h4 className="mb-0" style={{ color: COLORS.WHITE }}>Statistics</h4>
            </Card.Header>
          </Card>
        </Col>
      </Row>

      {/* Hike Summary Cards */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="stat-card">
            <Card.Body className="text-center">
              <div className="stat-number" style={{ color: COLORS.BUTTON_GREEN }}>
                {hikesCompleted}
              </div>
              <div className="stat-label">Hikes Completed</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="stat-card">
            <Card.Body className="text-center">
              <div className="stat-number" style={{ color: COLORS.PRIMARY_ORANGE }}>
                {hikesStarted}
              </div>
              <div className="stat-label">Hikes Started</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="stat-card">
            <Card.Body className="text-center">
              <div className="stat-number" style={{ color: COLORS.SECONDARY_GREEN }}>
                {monthlySubscribers}
              </div>
              <div className="stat-label">Monthly Subscribers</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="stat-card">
            <Card.Body className="text-center">
              <div className="stat-number" style={{ color: COLORS.MEDIUM_GREY }}>
                {hikesPerTrail.length}
              </div>
              <div className="stat-label">Trails with Hikes</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* User Routes Statistics */}
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Header style={{ backgroundColor: COLORS.PRIMARY_ORANGE, color: COLORS.WHITE }}>
              <h5 className="mb-0" style={{ color: COLORS.WHITE }}>User Routes Overview</h5>
            </Card.Header>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={3}>
          <Card className="stat-card">
            <Card.Body className="text-center">
              <div className="stat-number" style={{ color: COLORS.TERTIARY_DARK }}>
                {routeStats.totalRoutes}
              </div>
              <div className="stat-label">Total Routes</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="stat-card">
            <Card.Body className="text-center">
              <div className="stat-number" style={{ color: COLORS.PRIMARY_ORANGE }}>
                {routeStats.pendingReview}
              </div>
              <div className="stat-label">Pending Review</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="stat-card">
            <Card.Body className="text-center">
              <div className="stat-number" style={{ color: COLORS.BUTTON_GREEN }}>
                {routeStats.byStatus.active}
              </div>
              <div className="stat-label">Approved</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="stat-card">
            <Card.Body className="text-center">
              <div className="stat-number" style={{ color: COLORS.ERROR_RED }}>
                {routeStats.byStatus.inactive}
              </div>
              <div className="stat-label">Rejected</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={3}>
          <Card className="stat-card">
            <Card.Body className="text-center">
              <div className="stat-number" style={{ color: COLORS.MEDIUM_GREY }}>
                {routeStats.byState.draft}
              </div>
              <div className="stat-label">Draft</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="stat-card">
            <Card.Body className="text-center">
              <div className="stat-number" style={{ color: COLORS.SECONDARY_GREEN }}>
                {routeStats.byState.personal}
              </div>
              <div className="stat-label">Personal</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="stat-card">
            <Card.Body className="text-center">
              <div className="stat-number" style={{ color: COLORS.BUTTON_GREEN }}>
                {routeStats.byState.public}
              </div>
              <div className="stat-label">Public</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="stat-card">
            <Card.Body className="text-center">
              <div className="stat-number" style={{ color: COLORS.SECONDARY_GREEN }}>
                {approvalRate}%
              </div>
              <div className="stat-label">Approval Rate</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Top Route Creators Table */}
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Header style={{ backgroundColor: COLORS.PRIMARY_ORANGE, color: COLORS.WHITE }}>
              <h5 className="mb-0" style={{ color: COLORS.WHITE }}>Top Route Creators</h5>
            </Card.Header>
            <Card.Body>
              {topCreators.length > 0 ? (
                <div className="table-responsive">
                  <Table hover>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>User</th>
                        <th>Email</th>
                        <th>Total Routes</th>
                        <th>Approved</th>
                        <th>Pending</th>
                        <th>Rejected</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topCreators.map((creator, index) => (
                        <tr key={creator.userId}>
                          <td>{index + 1}</td>
                          <td><strong>{creator.userName}</strong></td>
                          <td className="text-muted">{creator.userEmail}</td>
                          <td><strong>{creator.totalRoutes}</strong></td>
                          <td>
                            <Badge bg="success">{creator.activeRoutes}</Badge>
                          </td>
                          <td>
                            <Badge bg="warning" text="dark">{creator.pendingRoutes}</Badge>
                          </td>
                          <td>
                            <Badge bg="danger">{creator.rejectedRoutes}</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <p className="text-muted mb-0">No route creators found</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Most Hiked and Most Saved Trails */}
      <Row className="mb-4">
        <Col md={6}>
          <Card>
            <Card.Header style={{ backgroundColor: COLORS.BUTTON_GREEN, color: COLORS.WHITE }}>
              <h5 className="mb-0" style={{ color: COLORS.WHITE }}>Most Hiked Trail</h5>
            </Card.Header>
            <Card.Body>
              {mostHikedTrail ? (
                <div>
                  <h4 style={{ color: COLORS.TERTIARY_DARK }}>{mostHikedTrail.trailName}</h4>
                  <p className="text-muted mb-0">
                    <strong>{mostHikedTrail.hikeCount}</strong> hike{mostHikedTrail.hikeCount !== 1 ? 's' : ''}
                  </p>
                </div>
              ) : (
                <p className="text-muted mb-0">No hikes found</p>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card>
            <Card.Header style={{ backgroundColor: COLORS.SECONDARY_GREEN, color: COLORS.WHITE }}>
              <h5 className="mb-0" style={{ color: COLORS.WHITE }}>Most Saved Trail</h5>
            </Card.Header>
            <Card.Body>
              {mostSavedTrail ? (
                <div>
                  <h4 style={{ color: COLORS.TERTIARY_DARK }}>{mostSavedTrail.trailName}</h4>
                  <p className="text-muted mb-0">
                    <strong>{mostSavedTrail.saveCount}</strong> user{mostSavedTrail.saveCount !== 1 ? 's' : ''} saved this trail
                  </p>
                </div>
              ) : (
                <p className="text-muted mb-0">No saved trails found</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Trail Rankings */}
      <Row className="mb-4">
        <Col md={6}>
          <Card>
            <Card.Header style={{ backgroundColor: COLORS.BUTTON_GREEN, color: COLORS.WHITE }}>
              <h5 className="mb-0" style={{ color: COLORS.WHITE }}>Trails Ranked by Completed Hikes</h5>
            </Card.Header>
            <Card.Body>
              {trailsRankedByCompleted.length > 0 ? (
                <div className="table-responsive">
                  <Table hover>
                    <thead>
                      <tr>
                        <th style={{ width: '50px' }}>Rank</th>
                        <th>Trail Name</th>
                        <th>Completed Hikes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trailsRankedByCompleted.map((trail, index) => (
                        <tr key={trail.trailId}>
                          <td>
                            <strong style={{ color: COLORS.BUTTON_GREEN }}>
                              #{index + 1}
                            </strong>
                          </td>
                          <td>{trail.trailName}</td>
                          <td>
                            <strong>{trail.hikeCount}</strong>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <p className="text-muted mb-0">No completed hikes found</p>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card>
            <Card.Header style={{ backgroundColor: COLORS.PRIMARY_ORANGE, color: COLORS.WHITE }}>
              <h5 className="mb-0" style={{ color: COLORS.WHITE }}>Trails Ranked by Attempted Hikes</h5>
            </Card.Header>
            <Card.Body>
              {trailsRankedByAttempted.length > 0 ? (
                <div className="table-responsive">
                  <Table hover>
                    <thead>
                      <tr>
                        <th style={{ width: '50px' }}>Rank</th>
                        <th>Trail Name</th>
                        <th>Attempted Hikes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trailsRankedByAttempted.map((trail, index) => (
                        <tr key={trail.trailId}>
                          <td>
                            <strong style={{ color: COLORS.PRIMARY_ORANGE }}>
                              #{index + 1}
                            </strong>
                          </td>
                          <td>{trail.trailName}</td>
                          <td>
                            <strong>{trail.hikeCount}</strong>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <p className="text-muted mb-0">No attempted hikes found</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Hikes Per Trail Table */}
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Header style={{ backgroundColor: COLORS.BUTTON_GREEN, color: COLORS.WHITE }}>
              <h5 className="mb-0" style={{ color: COLORS.WHITE }}>Hikes Count Per Trail</h5>
            </Card.Header>
            <Card.Body>
              {hikesPerTrail.length > 0 ? (
                <div className="table-responsive">
                  <Table hover>
                    <thead>
                      <tr>
                        <th>Trail Name</th>
                        <th>Hike Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {hikesPerTrail.map((trail) => (
                        <tr key={trail.trailId}>
                          <td>{trail.trailName}</td>
                          <td>
                            <strong>{trail.hikeCount}</strong>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <p className="text-muted mb-0">No hikes found</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Onboarding Statistics */}
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Header style={{ backgroundColor: COLORS.BUTTON_GREEN, color: COLORS.WHITE }}>
              <h5 className="mb-0" style={{ color: COLORS.WHITE }}>Onboarding Statistics</h5>
            </Card.Header>
            <Card.Body>
              {onboardingStats.length > 0 ? (
                <Row>
                  {onboardingStats.map((stat, index) => (
                    <Col md={6} key={index} className="mb-4">
                      <Card className="h-100 border-0 shadow-sm">
                        <Card.Body>
                          <h6 className="mb-3 font-weight-bold">{stat.question}</h6>
                          <div className="table-responsive">
                            <Table size="sm" borderless>
                              <thead>
                                <tr className="text-muted border-bottom">
                                  <th>Answer</th>
                                  <th className="text-end">Count</th>
                                </tr>
                              </thead>
                              <tbody>
                                {stat.answers?.map((answer, ansIndex) => (
                                  <tr key={ansIndex}>
                                    <td>{answer.answer}</td>
                                    <td className="text-end">
                                      <span className="badge bg-light text-dark border">
                                        {answer.count}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              ) : (
                <p className="text-muted mb-0">No onboarding statistics found</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Statistics;
